<script lang="ts" setup>
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'

withDefaults(
  defineProps<{
    isCurrent?: boolean
    name: string
    description: string
    priceLabel: string
    priceInterval?: 'year' | 'month' | null
    previousName?: string | null
    talkToSalesUrl?: string | null
    features: string[]
  }>(),
  {
    priceInterval: null,
    previousName: null,
    talkToSalesUrl: null,
  },
)

const emit = defineEmits<{ (e: 'upgrade-monthly' | 'upgrade-yearly'): void }>()
</script>

<template>
  <div
    class="plan"
    :class="isCurrent && 'plan--current'"
  >
    <div class="plan__top">
      <div class="plan__top__name">{{ name }}</div>
      <div class="plan__top__price">
        <div class="plan__top__price__amount">
          {{ priceLabel }}
        </div>
        <div
          v-if="priceInterval"
          class="plan__top__price__interval"
        >
          /{{ priceInterval }}
        </div>
      </div>
      <div class="plan__top__summary plan-summary">
        <span>
          {{ description }}
        </span>
        <a
          v-if="name === 'Starter'"
          class="plan-summary__link"
          href="https://www.v7labs.com/darwin-starter-plan"
        >
          View details
        </a>
      </div>
    </div>
    <div class="plan__bottom">
      <div class="plan__bottom__features">
        <div
          v-if="previousName"
          class="plan__bottom__features__previous"
        >
          Everything in {{ previousName }}, plus:
        </div>
        <div
          v-for="feature in features"
          :key="feature"
          class="plan__bottom__features__feature"
        >
          <Icon
            name="mono-check"
            class="plan__bottom__features__feature__icon"
          />
          {{ feature }}
        </div>
      </div>
      <DarwinButton
        v-if="isCurrent"
        class="plan__bottom__button"
        kind="secondary"
        size="md"
        flair="rounded"
        disabled
      >
        Current Plan</DarwinButton
      >
      <DarwinButton
        v-else-if="!isCurrent && !!priceInterval"
        class="plan__bottom__button"
        kind="primary"
        size="md"
        flair="rounded"
        @click="emit('upgrade-monthly')"
        >Upgrade</DarwinButton
      >
      <DarwinButton
        v-else
        class="plan__bottom__button"
        kind="secondary"
        size="md"
        flair="rounded"
        :to="talkToSalesUrl || undefined"
        >Contact Sales</DarwinButton
      >
    </div>
    <div
      v-if="!isCurrent && !!priceInterval"
      class="plan__below"
    >
      Save 20% by
      <a
        class="plan__below__link"
        @click="emit('upgrade-yearly')"
        >paying yearly</a
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
.plan {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 240px;
  height: 360px;
  flex-shrink: 0;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--rounded-corner-12);

  &--current {
    background: var(--color-surface-secondary);
    border: 1px solid var(--color-border-transparent);
  }

  &__top {
    height: 120px;
    padding: var(--size-4);
    padding-bottom: 0;

    &__name {
      @include textXS11pxDefault;
      color: var(--color-text-selected);
    }

    &__price {
      display: flex;
      flex-direction: row;
      align-items: flex-end;

      &__amount {
        @include textXL18pxBold;
        color: var(--color-text-default);
      }

      &__interval {
        height: 20px;
        padding: 2px 0 2px 0;
        @include textXS11pxDefault;
        color: var(--color-text-subtlest);
      }
    }
  }

  .plan-summary {
    @include textXS11pxLight;
    color: var(--color-text-subtle);
    display: flex;
    flex-direction: column;
    gap: var(--size-half);

    &__link {
      @include textXS11pxBold;
      color: var(--color-text-default);
      cursor: pointer;
    }
  }

  &__bottom {
    flex-grow: 1;

    padding: var(--size-4);
    padding-top: 0;
    display: flex;
    flex-direction: column;
    gap: var(--size-4);

    &__features {
      flex-grow: 1;
      border-top: 1px solid var(--color-border-subtle);
      padding-top: var(--size-4);

      display: flex;
      flex-direction: column;
      gap: var(--size-half);

      &__previous,
      &__feature {
        @include textXS11pxLight;
        color: var(--color-text-subtle);
      }

      &__feature {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: var(--size-2);

        &__icon {
          height: 16px;
          width: 16px;
          min-height: 0;
          color: var(--color-icon-subtlest);
        }
      }
    }

    &__button {
      width: 100%;
    }
  }

  &__below {
    @include textXS11pxLight;
    color: var(--color-text-subtle);

    padding-top: var(--size-2);
    position: absolute;
    top: 100%;
    text-align: center;
    width: 100%;

    &__link {
      @include textXS11pxBold;
      color: var(--color-text-selected);
      cursor: pointer;
    }
  }
}
</style>
